/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React from "react";

import { useDispatch } from "react-redux";

import Pagination from "rc-pagination";

import {
  DetailsList,
  DetailsListLayoutMode,
  Dropdown,
  IColumn,
  SelectionMode,
  Spinner,
  SpinnerSize,
} from "@fluentui/react";

import * as VisualStudioBenefitActions from "Dataflows/VisualStudioBenefit/VisualStudioBenefitAction";

import ChevronIcon from "Components/Common/ChevronIcon/ChevronIcon";
import { PaginationRow } from "Components/Common/Pagination/Pagination.styles";
import { detailListStyles, spinnerStyles } from "Components/Common/SharedListStyles.styles";
import StatusElement from "Components/Common/Status/StatusElement";
import { VisualStudioBenefitStatusEnum } from "Constants/VisualStudioBenefitConstants";
import { IBenefit } from "Interfaces/Benefits/IBenefit";
import { IVisualStudioBenefit } from "Interfaces/VisualStudioBenefits/IVisualStudioBenefit";
import { formatDate } from "Utility/FormatHelper";

import { IVisualStudioBenefitsListProps } from "./IVisualStudioBenefitsListProps";

const VisualStudioBenefitsList: React.FunctionComponent<IVisualStudioBenefitsListProps> = (
  props
) => {
  const dispatch = useDispatch();

  const { data, loadBenefitsError, isLoadingBenefits, filteredCount, currentPage, pageSize } =
    props;

  const formatDisplayDate = (incomingDate: Date | undefined): React.ReactElement => {
    if (incomingDate) {
      return <span>{formatDate(incomingDate)}</span>;
    } else {
      return <></>;
    }
  };

  const formatStatusColumn = (statusKey: string): React.ReactElement => {
    return <StatusElement statusValue={VisualStudioBenefitStatusEnum[statusKey]} />;
  };

  const columns: IColumn[] = [
    {
      key: "userId",
      name: "User ID",
      ariaLabel: "User ID",
      fieldName: "userId",
      minWidth: 80,
      isResizable: true,
    },
    {
      key: "assignedTo",
      name: "Assigned to",
      ariaLabel: "Assigned to",
      fieldName: "assignedTo",
      minWidth: 160,
      isResizable: true,
    },
    {
      key: "claimedBy",
      name: "Claimed by",
      ariaLabel: "Claimed by",
      fieldName: "claimedBy",
      minWidth: 160,
      isResizable: true,
    },
    {
      key: "status",
      name: "Status",
      ariaLabel: "Status",
      fieldName: "status",
      minWidth: 100,
      onRender: (item: IBenefit): React.ReactElement => {
        return formatStatusColumn(item.status);
      },
      isResizable: true,
    },
    {
      key: "subscriptionGuid",
      name: "Code",
      ariaLabel: "Code",
      fieldName: "subscriptionGuid",
      minWidth: 100,
      isResizable: true,
      isSorted: false,
    },
    {
      key: "expirationDate",
      name: "Code Expiration Date",
      ariaLabel: "Date code expires",
      fieldName: "expirationDate",
      minWidth: 160,
      onRender: (item: IVisualStudioBenefit): React.ReactElement => {
        return formatDisplayDate(item.expirationDate);
      },
    },
    {
      key: "lastManagedBy",
      name: "Last Managed By",
      ariaLabel: "Last managed by",
      fieldName: "lastManagedBy",
      minWidth: 160,
      isResizable: true,
    },
  ];

  if (isLoadingBenefits) {
    return <Spinner size={SpinnerSize.large} styles={spinnerStyles} />;
  } else if (loadBenefitsError !== "") {
    return <div>{loadBenefitsError}</div>;
  }

  return (
    <>
      <DetailsList
        items={data}
        columns={columns}
        selectionMode={SelectionMode.none}
        getKey={(item: IVisualStudioBenefit): string => `${item.assignedTo}${item.createdDate}`}
        setKey="none"
        layoutMode={DetailsListLayoutMode.fixedColumns}
        isHeaderVisible={true}
        enterModalSelectionOnTouch={true}
        styles={detailListStyles}
      />
      <PaginationRow>
        <Pagination
          total={filteredCount}
          onChange={(page) => dispatch(VisualStudioBenefitActions.updateCurrentPage(page))}
          showTitle={false}
          current={currentPage}
          prevIcon={ChevronIcon("ChevronLeft")}
          nextIcon={ChevronIcon("ChevronRight")}
          pageSize={pageSize}
          showSizeChanger
        />
        <Dropdown
          options={[
            { key: 16, text: "Show 16 results" },
            { key: 32, text: "Show 32 results" },
            { key: 48, text: "Show 48 results" },
          ]}
          onChange={(_e, op): void => {
            dispatch(VisualStudioBenefitActions.updatePageSize(op?.key as number));
          }}
          selectedKey={pageSize}
          className="pagination-dropdown"
          title="Page Size Dropdown"
        />
      </PaginationRow>
    </>
  );
};

export default VisualStudioBenefitsList;
